import React from "react";
import * as Mui from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { themes } from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";
import { getContrastingTextColor } from "services/utils/colorChecker";

const inactiveChipSx = { borderRadius: "6px", height: 30, fontWeight: 600 };

// Shared "All Headers" + saved-view chip row, paired with useSavedHeaderViews
// and SaveViewButton. Pass the current scope's views/active id plus the
// handlers from useSavedHeaderViews, and an onAddNew that opens SaveViewButton
// in create mode (e.g. `(event) => saveViewButtonRef.current?.openCreate(event.currentTarget)`).
//
// `activeColor` defaults to the fixed brand green used everywhere else —
// different screens wrap (or skip) their own local MUI ThemeProvider, so
// `color="primary"` would resolve differently per screen, and a hardcoded
// default keeps this visually consistent across all of them. Pass a screen's
// own accent color explicitly (e.g. from useSaveView's `chipColor` option)
// only when that screen has its own established color language to match.
export const SavedViewChips = ({
  views = [],
  activeViewId = null,
  onSelectView,
  onShowAllHeaders,
  onDeleteView,
  onAddNew,
  disabled = false,
  activeColor = themes.primaryIndicator,
}) => {
  const activeChipSx = {
    ...inactiveChipSx,
    backgroundColor: activeColor,
    color: getContrastingTextColor(activeColor),
    "&:hover": { backgroundColor: activeColor },
    "& .MuiChip-deleteIcon": {
      color: getContrastingTextColor(activeColor),
    },
  };

  return (
    <Mui.Stack
      direction="row"
      spacing={1}
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
    >
      <Mui.Chip
        label="All Headers"
        clickable
        variant={activeViewId ? "outlined" : "filled"}
        sx={activeViewId ? inactiveChipSx : activeChipSx}
        onClick={onShowAllHeaders}
      />
      {views.map((view) => (
        <Mui.Chip
          key={view.id}
          label={view.name}
          clickable
          variant={activeViewId === view.id ? "filled" : "outlined"}
          onClick={() => onSelectView(view.id)}
          onDelete={(event) => {
            event.stopPropagation();
            onDeleteView(view.id);
          }}
          deleteIcon={<CloseIcon fontSize="small" />}
          sx={activeViewId === view.id ? activeChipSx : inactiveChipSx}
        />
      ))}
      <Mui.Tooltip slotProps={tooltipStyleProps} title="Add View" arrow>
        <span>
          <Mui.IconButton
            size="small"
            disabled={disabled}
            onClick={onAddNew}
            sx={{
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: "6px",
              height: 30,
              width: 30,
            }}
          >
            <AddIcon fontSize="small" />
          </Mui.IconButton>
        </span>
      </Mui.Tooltip>
    </Mui.Stack>
  );
};
